import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { throwError } from 'rxjs';
import { InterestedPerson, Location, SiteMeta } from 'src/app/models/common-models';
import { LogService } from 'src/app/services/common/log.service';
import { Utils } from 'src/app/services/common/utils';
import { EnvironmentService } from 'src/app/services/environment.service';
import { InterestedPersonService } from 'src/app/services/interested-person.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-prospect-form',
  templateUrl: './prospect-form.component.html',
  styleUrls: ['./prospect-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ProspectFormComponent implements OnInit {
  
  @Input() public locationId : number;
  @Input() public location : Location;
  @Input() public siteMeta : SiteMeta;
  @Output() close = new EventEmitter<void>();

  isLoading = true;
  submitted: boolean = false;
  
  agreedTermsOfService: boolean = false;
  isSmsNotificationVisible: boolean = false;
  isButtonDisabled: boolean = true;
  
  prospectForm!: FormGroup;
  prospectRequest: InterestedPerson = {
    LocationId: 0, FirstName: '', LastName: '', DialCode: '+1', PhoneNumber: '', Email: '', EmailNotification: true, SmsNotification: false, FullName: '', Gender: '', BirthDate: '', Interests: '', AddressLine1: '', AddressLine2: '', City: '', State: '', ZipCode: '', Country: '', MenuPackId: 0, PaymentMethod: 'us_bank_account', PurchaseId: '', PurchasedAt: null, Amount: null, PurchaseDescription: '', CreatedAt: '', Guid: '', Id: 0
  };

  countryCode = 'us';
  flagUrl: string = `https://dropfitness-static.nyc3.digitaloceanspaces.com/static/svg/flags/4x3/us.svg`;
  dialCode = "+1";

  logo = '';
  
  constructor(
    private logService: LogService,
    private fb: FormBuilder,
    public activeModal: NgbActiveModal,
    private _environmentService: EnvironmentService,
    private interestedPersonService: InterestedPersonService
  ) {
    
  }

  async ngOnInit() {
    this.isLoading = true;
    this.initForm(this.prospectRequest);
    this.isLoading = false;
    this.logo = this._environmentService.getLinkUrlById('smallLogo');
  }

  initForm(prospectRequest?: InterestedPerson): void {
    const initial: InterestedPerson = prospectRequest || {
      LocationId: 0, FirstName: '', LastName: '', DialCode: '+1', PhoneNumber: '', Email: '', EmailNotification: true, SmsNotification: false, FullName: '', Gender: '', BirthDate: '', Interests: '', AddressLine1: '', AddressLine2: '', City: '', State: '', ZipCode: '', Country: '', MenuPackId: 0, PaymentMethod: 'us_bank_account', PurchaseId: '', PurchasedAt: null, Amount: 0, PurchaseDescription: '', CreatedAt: '', Guid: '', Id: 0
    };
    this.prospectRequest = initial;
    
    this.prospectForm = this.fb.group({
      LocationId: [initial.LocationId, [Validators.required]],
      FirstName: [initial.FirstName, [Validators.required]],
      LastName: [initial.LastName, [Validators.required]],
      DialCode: [initial.DialCode, [Validators.required]],
      PhoneNumber: [initial.PhoneNumber, [Validators.required, Validators.pattern('^[0-9]{10}$')]],
      Email: [initial.Email, [Validators.required, Validators.email]],
      PaymentMethod: [initial.PaymentMethod, []],
      EmailNotification: [initial.EmailNotification, []],
      SmsNotification: [initial.SmsNotification, []],
    });

    // Detect when FirstName changes
    this.prospectForm.get('FirstName')?.valueChanges.subscribe(value => {
      this.submitButtonToggle();
    });

    // Detect when LastName changes
    this.prospectForm.get('LastName')?.valueChanges.subscribe(value => {
      this.submitButtonToggle();
    });

    // Detect when DialCode changes
    this.prospectForm.get('DialCode')?.valueChanges.subscribe(value => {
      this.submitButtonToggle();
    });

    // Detect when PhoneNumber changes
    this.prospectForm.get('PhoneNumber')?.valueChanges.subscribe(value => {
      this.submitButtonToggle();
    });

    // Detect when Email changes
    this.prospectForm.get('Email')?.valueChanges.subscribe(value => {
      this.submitButtonToggle();
    });
  }

  // To trigger validation on focus out
  validateField(fieldName: string): void {
    const control = this.prospectForm.get(fieldName);
    if (control) {
      control.markAsTouched();
      control.updateValueAndValidity();
    }
  }

  onTermsOfServiceChange(event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    this.agreedTermsOfService = checkbox.checked;
    this.submitButtonToggle();
  }

  onSmsNotificationChange(event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    this.isSmsNotificationVisible = checkbox.checked;
  }

  submitButtonToggle(): void {
    if(this.prospectForm.get('FirstName')?.valid && this.prospectForm.get('LastName')?.valid && this.prospectForm.get('Email')?.valid && this.prospectForm.get('DialCode')?.valid && this.prospectForm.get('PhoneNumber')?.valid && !this.isLoading && this.agreedTermsOfService){
      this.isButtonDisabled = false;
    } else {
      this.isButtonDisabled = true;
    }
  }
  
  formatCurrency(amount: number){
    return Utils.formatCurrency(amount);
  }

  toTitleCase(input: string){
    return Utils.toTitleCase(input);
  }

  async onSubmit() {
    this.submitted = true;
    if(!this.prospectForm.valid)
    {
      this.isButtonDisabled = true;
      return;
    }
    try {
      // Check if both fields are valid
      if (this.prospectForm.get('FirstName')?.valid && this.prospectForm.get('LastName')?.valid && this.prospectForm.get('Email')?.valid && this.prospectForm.get('DialCode')?.valid && this.prospectForm.get('PhoneNumber')?.valid) {
        var request : Partial<InterestedPerson> = {
          LocationId: this.locationId,
          DialCode: this.prospectForm.get('DialCode')?.value,
          PhoneNumber: this.prospectForm.get('PhoneNumber')?.value,
          FirstName: this.prospectForm.get('FirstName')?.value,
          LastName: this.prospectForm.get('LastName')?.value,
          Email: this.prospectForm.get('Email')?.value,
          PaymentMethod: this.prospectForm.get('PaymentMethod')?.value,
          EmailNotification: this.prospectForm.get('EmailNotification')?.value,
          SmsNotification: this.prospectForm.get('SmsNotification')?.value
        };

        // Save form details in local storage
        sessionStorage.setItem('prospectInfo', JSON.stringify(request));

        this.interestedPersonService.saveInterestedPerson(request).subscribe(async (result: InterestedPerson) => {
          this.logService.info('Prospect form submitted', result);
          this.submitted = false;
          this.closeModal(true);
          this.close.emit();
        }, (error) => {
          this.logService.error('Error submitting prospect form', error);
          this.isButtonDisabled = true;
          this.submitted = false;
        });
      }
    } catch (err) {
      this.logService.error('Error fetching data', err);
      return throwError(() => err);
    } finally {
      return;
    }
  }

  public open() {
    setTimeout(() => {
      // Add inert attribute to background content when modal is open
      const mainContent = document.querySelector('main');
      if (mainContent) {
        mainContent.setAttribute('inert', 'true');
      }
    });
  }

  closeModal(result: boolean) {
    // Remove inert attribute from background content when modal is closed
    const mainContent = document.querySelector('main');
    if (mainContent) {
      mainContent.removeAttribute('inert');
    }
    this.activeModal.close(result);
  }

  flag(): string {
    return `https://dropfitness-static.nyc3.digitaloceanspaces.com/static/svg/flags/4x3/${this.countryCode}.svg`;
  }

  changeCountry(ctry: any) {
    this.countryCode = ctry.country;
    this.flagUrl = `https://dropfitness-static.nyc3.digitaloceanspaces.com/static/svg/flags/4x3/${this.countryCode}.svg`;
    this.dialCode = ctry.phoneCode;
    this.prospectForm.patchValue({
      DialCode: ctry.phoneCode
    });
  }

  closeForm() {
    this.close.emit();
  }
}
